import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Shield, Sparkles, FileText, Plus, ShieldCheck } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const BAND_STYLE = {
  likely_adequate:    { color: "var(--success)", label: "Likely adequate" },
  likely_under:       { color: "var(--alert)",   label: "Likely under-insured" },
  likely_over:        { color: "var(--blue-dark)", label: "Likely over-insured" },
  insufficient_data:  { color: "var(--warning)", label: "Insufficient data" },
};

const $ = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const daysUntil = (iso) => Math.round((new Date(iso) - Date.now()) / 86400000);

export default function InsuranceDetail() {
  const { policy_id } = useParams();
  const { user } = useAuth();
  const schemeId = getActiveSchemeId();
  const [policy, setPolicy] = useState(null);
  const [valuations, setValuations] = useState([]);
  const [me, setMe] = useState(null);
  const [adequacy, setAdequacy] = useState(null);
  const [adequacyBusy, setAdequacyBusy] = useState(false);
  const [adequacyRateLimit, setAdequacyRateLimit] = useState(null);
  const [valOpen, setValOpen] = useState(false);

  const reload = async () => {
    const [p, v, members] = await Promise.all([
      api.get(`/schemes/${schemeId}/insurance/${policy_id}`),
      api.get(`/schemes/${schemeId}/insurance/${policy_id}/valuations`),
      api.get(`/schemes/${schemeId}/members`),
    ]);
    setPolicy(p.data); setValuations(v.data);
    const mine = members.data.find((m) => m.user_id === user?.user_id);
    setMe(mine || (user?.system_role === "platform_admin" ? { role: "chair" } : null));
  };
  useEffect(() => { reload().catch((e) => toast.error(apiError(e))); /* eslint-disable-next-line */ }, [policy_id]);

  const [adequacyStreaming, setAdequacyStreaming] = useState(false);
  const [adequacyLive, setAdequacyLive] = useState("");

  if (!policy) return <AppShell><div className="max-w-4xl mx-auto px-5 py-10 text-sm" style={{ color: "var(--slate)" }}>Loading…</div></AppShell>;

  const isChairOrCommittee = me?.role === "chair" || me?.role === "committee";
  const isOwner = !isChairOrCommittee;
  const days = daysUntil(policy.period_end);
  const daysColor = days < 0 ? "var(--alert)" : days < 30 ? "var(--alert)" : days < 60 ? "var(--warning)" : "var(--success)";

  const runAdequacy = async () => {
    setAdequacy(null); setAdequacyRateLimit(null); setAdequacyBusy(true);
    setAdequacyStreaming(true); setAdequacyLive("");
    const { streamSSE } = await import("@/lib/sseStream");
    const token = localStorage.getItem("opstrai.session_token");
    await streamSSE({
      url: `${process.env.REACT_APP_BACKEND_URL}/api/ai/insurance/check-adequacy`,
      body: { scheme_id: schemeId, policy_id },
      headers: { Authorization: `Bearer ${token}` },
      onEvent: ({ event, data }) => {
        if (event === "token") { setAdequacyBusy(false); setAdequacyLive((p) => p + (data?.text || "")); }
        else if (event === "done") {
          setAdequacy(data); setAdequacyStreaming(false); setAdequacyBusy(false); setAdequacyLive("");
          reload();
        } else if (event === "error") {
          toast.error(data?.message || "AI provider error");
          setAdequacyStreaming(false); setAdequacyBusy(false);
        }
      },
      onError: (err) => {
        if (err?.status === 429) {
          const secs = parseInt(err?.retryAfter || "3600", 10);
          const mins = Math.max(1, Math.ceil(secs / 60));
          setAdequacyRateLimit(`AI request limit reached. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`);
        } else { toast.error(err?.message || "Failed"); }
        setAdequacyStreaming(false); setAdequacyBusy(false);
      },
    });
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-6 lg:py-8" data-testid="insurance-detail-page">
        <Link to="/scheme/insurance" className="inline-flex items-center gap-1 text-xs font-medium mb-4 hover:underline" style={{ color: "var(--slate)" }} data-testid="back-to-insurance">
          <ArrowLeft size={12}/> All policies
        </Link>

        {/* Header */}
        <div className="rounded-2xl border bg-white p-5 lg:p-6 mb-5" style={{ borderColor: "var(--border)" }}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }} data-testid="detail-policy-type">{policy.policy_type.replace(/_/g, " ")}</span>
              <h1 className="text-2xl font-bold mt-2" style={{ color: "var(--navy)" }} data-testid="detail-insurer">{policy.insurer_name}</h1>
              <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>Policy {policy.policy_number}</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "var(--slate)" }}>Renews</div>
              <div className="text-2xl font-bold tabular-nums" style={{ color: daysColor }} data-testid="detail-countdown">{days < 0 ? `${-days}d ago` : `in ${days}d`}</div>
              <div className="text-[10px]" style={{ color: "var(--tagline-grey)" }}>{new Date(policy.period_end).toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        {/* Facts */}
        <Section title="Policy facts" testid="detail-facts">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Fact label="Sum insured" value={$(policy.sum_insured)}/>
            {!isOwner && <Fact label="Annual premium" value={$(policy.premium)} testid="detail-premium"/>}
            <Fact label="Last valuation" value={policy.last_valuation_amount ? $(policy.last_valuation_amount) : "—"}/>
            <Fact label="Valuation date" value={policy.last_valuation_date ? new Date(policy.last_valuation_date).toLocaleDateString() : "—"}/>
            {!isOwner && policy.broker_name && <Fact label="Broker" value={policy.broker_name} testid="detail-broker"/>}
            {!isOwner && policy.broker_contact_email && <Fact label="Broker contact" value={policy.broker_contact_email}/>}
          </div>
          {policy.notes && <p className="text-sm mt-4 pt-3 border-t" style={{ borderColor: "var(--border)", color: "var(--slate)" }}>{policy.notes}</p>}
          {policy.linked_compliance_item_id && (
            <Link to={`/scheme/compliance/${policy.linked_compliance_item_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded mt-4 hover:underline" style={{ background: "var(--success-light)", color: "var(--success)" }} data-testid="detail-compliance-link">
              <ShieldCheck size={10}/> Linked compliance item
            </Link>
          )}
        </Section>

        {/* Valuations */}
        <Section title="Valuation history" testid="detail-valuations" action={
          isChairOrCommittee && (
            <Dialog open={valOpen} onOpenChange={setValOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="record-valuation-btn"><Plus size={12} className="mr-1.5"/> Record valuation</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <ValuationForm onSubmit={async (form) => {
                  try { await api.post(`/schemes/${schemeId}/insurance/${policy_id}/valuations`, form); toast.success("Valuation recorded"); setValOpen(false); reload(); }
                  catch (e) { toast.error(apiError(e)); }
                }} onCancel={() => setValOpen(false)}/>
              </DialogContent>
            </Dialog>
          )
        }>
          {valuations.length === 0 ? (
            <p className="text-xs py-2" style={{ color: "var(--tagline-grey)" }}>No valuations on file.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  <th className="py-2">Date</th><th>Amount</th><th>Valuer</th><th>Notes</th>
                </tr>
              </thead>
              <tbody data-testid="valuations-table">
                {valuations.map((v) => (
                  <tr key={v.valuation_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`valuation-row-${v.valuation_id}`}>
                    <td className="py-2 tabular-nums" style={{ color: "var(--navy)" }}>{new Date(v.valuation_date).toLocaleDateString()}</td>
                    <td className="font-mono font-bold tabular-nums" style={{ color: "var(--navy)" }}>{$(v.amount)}</td>
                    <td style={{ color: "var(--slate)" }}>{v.valuer_name}</td>
                    <td className="text-xs" style={{ color: "var(--tagline-grey)" }}>{v.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Linked documents */}
        {policy.document_ids?.length > 0 && (
          <Section title="Linked documents" testid="detail-documents">
            <ul className="space-y-1 text-sm">
              {policy.document_ids.map((did) => (
                <li key={did} className="flex items-center gap-2" style={{ color: "var(--blue-dark)" }}>
                  <FileText size={14}/> <Link to={`/scheme/documents`} className="hover:underline">{did}</Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* AI Adequacy */}
        <Section title="AI adequacy check" testid="detail-adequacy" action={
          isChairOrCommittee && (
            <Button size="sm" disabled={adequacyBusy} onClick={runAdequacy} style={{ background: "var(--blue)", color: "white" }} data-testid="adequacy-btn">
              <Sparkles size={12} className="mr-1.5"/> {adequacyBusy ? "Running…" : (policy.latest_adequacy_check ? "Re-run check" : "Run check")}
            </Button>
          )
        }>
          {adequacyRateLimit && (
            <div className="rounded-xl border p-3 mb-3" style={{ borderColor: "var(--warning)", background: "var(--warning-light)" }} data-testid="adequacy-rate-limit">
              <p className="text-xs" style={{ color: "var(--navy)" }}>{adequacyRateLimit}</p>
            </div>
          )}
          {isOwner ? (
            policy.latest_adequacy_check ? (
              <p className="text-sm" data-testid="owner-adequacy-line" style={{ color: "var(--slate)" }}>
                Adequacy last assessed: {new Date(policy.latest_adequacy_check.ran_at).toLocaleDateString()} — <strong style={{ color: BAND_STYLE[policy.latest_adequacy_check.band]?.color }}>{BAND_STYLE[policy.latest_adequacy_check.band]?.label || policy.latest_adequacy_check.band}</strong>
              </p>
            ) : (
              <p className="text-xs" style={{ color: "var(--tagline-grey)" }}>No adequacy assessment has been run yet by the chair / committee.</p>
            )
          ) : adequacyStreaming && adequacyLive ? (
            <div data-testid="adequacy-streaming">
              <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--blue)" }}>
                <span className="ai-dot"/> Streaming…
              </div>
              <pre className="whitespace-pre-wrap text-xs font-sans" style={{ color: "var(--navy)" }} data-testid="adequacy-live">{adequacyLive}</pre>
            </div>
          ) : adequacy ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--blue-light)", color: BAND_STYLE[adequacy.adequacy_band]?.color }} data-testid="adequacy-band">{BAND_STYLE[adequacy.adequacy_band]?.label || adequacy.adequacy_band}</span>
                <span className="text-2xl font-bold tabular-nums" style={{ color: BAND_STYLE[adequacy.adequacy_band]?.color }} data-testid="adequacy-score">{adequacy.score}/100</span>
                <span className="text-[10px]" style={{ color: "var(--tagline-grey)" }}>{adequacy.model}</span>
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm" data-testid="adequacy-reasoning" style={{ color: "var(--navy)" }}>{adequacy.reasoning}</div>
              {adequacy.suggested_actions?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--slate)" }}>Suggested actions</div>
                  <ul className="space-y-1 text-sm">
                    {adequacy.suggested_actions.map((a, i) => <li key={i} className="flex gap-2"><span style={{ color: "var(--blue)" }}>•</span><span style={{ color: "var(--navy)" }}>{a}</span></li>)}
                  </ul>
                </div>
              )}
              <p className="text-[11px] mt-4 pt-3 border-t italic" style={{ borderColor: "var(--border)", color: "var(--tagline-grey)" }}>{adequacy.disclaimer}</p>
            </>
          ) : policy.latest_adequacy_check ? (
            <p className="text-sm" style={{ color: "var(--slate)" }}>
              Last assessment: <strong style={{ color: BAND_STYLE[policy.latest_adequacy_check.band]?.color }}>{BAND_STYLE[policy.latest_adequacy_check.band]?.label}</strong> ({policy.latest_adequacy_check.score}/100) · {new Date(policy.latest_adequacy_check.ran_at).toLocaleDateString()}. Click "Re-run check" to refresh.
            </p>
          ) : (
            <p className="text-xs" style={{ color: "var(--tagline-grey)" }}>No assessment yet. Click "Run check" to generate.</p>
          )}
        </Section>

        {/* Sprint 1 Week 1 — Commission disclosure (AFSL transparency) */}
        <CommissionDisclosurePanel policy={policy} isChair={isChairOrCommittee} schemeId={schemeId} onSaved={reload}/>
      </div>
    </AppShell>
  );
}

function CommissionDisclosurePanel({ policy, isChair, schemeId, onSaved }) {
  const [disclosure, setDisclosure] = useState("");
  const [editing, setEditing] = useState(false);
  const [bc, setBc] = useState(policy.broker_commission_percent ?? "");
  const [pf, setPf] = useState(policy.platform_fee_percent ?? "");
  const [bm, setBm] = useState(!!policy.burmac_referred);
  useEffect(() => { api.get("/legal/afsl-disclosure").then((r) => setDisclosure(r.data.disclosure || "")).catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.patch(`/schemes/${schemeId}/insurance/${policy.policy_id}`, {
        broker_commission_percent: bc === "" ? null : Number(bc),
        platform_fee_percent: pf === "" ? null : Number(pf),
        burmac_referred: bm,
      });
      setEditing(false); onSaved && onSaved();
    } catch {}
  };
  const bcPct = policy.broker_commission_percent;
  const pfPct = policy.platform_fee_percent;
  return (
    <Section title="Commission disclosure" testid="detail-commission" action={isChair ? (
      <Button size="sm" variant="outline" onClick={() => setEditing(!editing)} data-testid="commission-edit-btn">{editing ? "Cancel" : "Edit"}</Button>
    ) : null}>
      {editing ? (
        <div className="space-y-2 text-sm">
          <label className="block text-xs" style={{ color: "var(--slate)" }}>Broker commission % <input type="number" step="0.01" value={bc} onChange={(e) => setBc(e.target.value)} className="ml-2 border rounded px-2 py-1 w-24" data-testid="commission-broker-input"/></label>
          <label className="block text-xs" style={{ color: "var(--slate)" }}>Platform fee % <input type="number" step="0.01" value={pf} onChange={(e) => setPf(e.target.value)} className="ml-2 border rounded px-2 py-1 w-24" data-testid="commission-platform-input"/></label>
          <label className="block text-xs" style={{ color: "var(--slate)" }}><input type="checkbox" checked={bm} onChange={(e) => setBm(e.target.checked)} className="mr-2" data-testid="commission-burmac-toggle"/>Referred to BurMac by Opstrait</label>
          <Button size="sm" onClick={save} style={{ background: "var(--blue)", color: "white" }} data-testid="commission-save-btn">Save</Button>
        </div>
      ) : (
        <div className="space-y-1.5 text-sm" style={{ color: "var(--navy)" }}>
          {isChair ? (
            <>
              {bcPct != null && <div>Broker commission: <strong>{bcPct}%</strong> of premium (paid to {policy.insurer || "broker"} by the insurer, not the owners corporation)</div>}
              {pfPct != null && pfPct > 0 && <div>Platform referral fee: <strong>{pfPct}%</strong> (paid to Opstrait by the broker)</div>}
              {policy.burmac_referred && <div className="text-xs inline-block px-2 py-0.5 rounded-full font-semibold mt-1" style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }} data-testid="commission-burmac-badge">This policy was referred to BurMac by Opstrait</div>}
              {bcPct == null && pfPct == null && !policy.burmac_referred && <p className="text-xs italic" style={{ color: "var(--tagline-grey)" }}>No commission or fees recorded yet. Click Edit to record disclosure.</p>}
            </>
          ) : (
            <>
              {bcPct != null && <div className="text-xs">This policy includes broker commission of <strong>{bcPct}%</strong>. Referral fees and platform fees are disclosed for transparency.</div>}
              {bcPct == null && <p className="text-xs italic" style={{ color: "var(--tagline-grey)" }}>No commission disclosed for this policy.</p>}
            </>
          )}
        </div>
      )}
      <p className="text-[11px] mt-4 pt-3 border-t italic" style={{ borderColor: "var(--border)", color: "var(--tagline-grey)" }} data-testid="commission-afsl-footer">{disclosure}</p>
    </Section>
  );
}

function Section({ title, children, action, testid }) {
  return (
    <div className="rounded-2xl border bg-white p-5 lg:p-6 mb-5" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--navy)" }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
function Fact({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>{label}</div>
      <div className="font-semibold text-sm mt-0.5" style={{ color: "var(--navy)" }}>{value}</div>
    </div>
  );
}
function ValuationForm({ onSubmit, onCancel }) {
  const [f, setF] = useState({ valuation_date: "", amount: 0, valuer_name: "", valuer_contact: "", notes: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <>
      <DialogHeader><DialogTitle>Record valuation</DialogTitle></DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, amount: Number(f.amount) }); }} className="space-y-3 mt-2" data-testid="valuation-form">
        <FF label="Valuation date" type="date" value={f.valuation_date} onChange={set("valuation_date")} testid="val-date" required/>
        <FF label="Amount ($)" type="number" step="0.01" value={f.amount} onChange={set("amount")} testid="val-amount" required/>
        <FF label="Valuer name" value={f.valuer_name} onChange={set("valuer_name")} testid="val-valuer" required/>
        <FF label="Valuer contact" value={f.valuer_contact} onChange={set("valuer_contact")} testid="val-contact"/>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Notes</Label>
          <Textarea className="mt-1" rows={2} value={f.notes} onChange={set("notes")} data-testid="val-notes"/>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" style={{ background: "var(--blue)", color: "white" }} data-testid="val-submit">Record</Button>
        </DialogFooter>
      </form>
    </>
  );
}
function FF({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>{label}</Label>
      <Input className="mt-1" data-testid={testid} {...rest}/>
    </div>
  );
}
